import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";
import cn from "classnames";

import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";

import { tagular } from "@cohesion/tagular";

import { PlayFill } from "@icons/play";
import { Loading } from "@components/loading";
import { DestinationContent } from "@pageComponents/best-in-travel-2025/destinationContent";

import { Background } from "./background";
import { SliderControls } from "./sliderControls";

export function DestinationSection({ destination, index, totalDestinations }) {
  const { destinationsMenuOpen, handleVideo, setLoading } = useBestInTravel();
  const { media } = destination;
  const [mediaIndex, setMediaIndex] = useState(0);
  const isVideo = media[mediaIndex]?.type === "video";

  // SWIPEABLE SLIDER CONTROLS - MOBILE
  const handleSwipeLeft = () => {
    setMediaIndex(prevIndex => (prevIndex + 1) % media.length);
  };

  const handleSwipeRight = () => {
    setMediaIndex(prevIndex => (prevIndex - 1 + media.length) % media.length);
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = e => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = e => {
    touchEndX.current = e.changedTouches[0].clientX;

    if (touchStartX.current - touchEndX.current > 10) {
      handleSwipeLeft();
    } else if (touchEndX.current - touchStartX.current > 10) {
      handleSwipeRight();
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const handleMediaSelect = i => {
    setLoading(true);
    setMediaIndex(i);
    tagular("click", {
      actionOutcome: "SLIDE",
      outboundUrl: "null",
      webElement: {
        location: "BIT_Gallery",
        elementType: "BUTTON",
        text: i > mediaIndex ? "NEXT ARROW" : "PREV ARROW",
        name: destination.name,
        position: "0",
      },
    });
  };

  // LAZY LOADING IMAGES
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  return (
    <div ref={ref} className="w-screen h-screen-dvh">
      <div className="relative flex flex-col h-full sm:flex-row">
        {/* CONTENT SECTION */}
        <DestinationContent
          destination={destination}
          index={index}
          totalDestinations={totalDestinations}
          activeMedia={mediaIndex}
          mediaCredits={!isVideo ? media[mediaIndex]?.credit : null}
          mediaItems={media.length}
          handleMediaSelect={handleMediaSelect}
        />
        {/* MEDIA SECTION */}
        <div className="relative w-full h-screen-dvh sm:w-2/3 sm:order-2">
          {/* IMAGES OR VIDEOS */}
          <div className="bg-black-100 h-screen-dvh">
            {inView ? (
              <>
                {!destinationsMenuOpen && (
                  //  SWIPEABLE SLIDER CONTROLS - MOBILE TOUCH AREA
                  <div
                    className="absolute z-10 w-full h-[calc(100vh-188px)] sm:hidden"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  />
                )}
                <Background media={media[mediaIndex]} />
              </>
            ) : (
              <div className="flex items-center justify-center h-screen-dvh bg-black-100">
                <Loading />
              </div>
            )}
          </div>
          {/* MEDIA CONTROLS */}

          <div className="container absolute justify-between hidden w-full top-4 sm:right-4 sm:w-auto sm:inline-block sm:h-20">
            {/* SLIDER CONTROLS */}
            {media?.length > 1 && (
              <SliderControls
                active={mediaIndex}
                length={media?.length}
                handleMediaSelect={handleMediaSelect}
              />
            )}
          </div>
          {/* CAPTIONS */}
          {!isVideo && (
            <div className="absolute hidden bottom-4 right-8 sm:block">
              <div
                className={cn(
                  "bg-black/70 w-content rounded px-2 py-1 text-right text-xs text-white"
                )}
              >
                {media[mediaIndex]?.credit || "Lonely Planet"}
              </div>
            </div>
          )}

          {/* PLAY VIDEO BTN */}
          {media[mediaIndex]?.type === "video" && !destinationsMenuOpen && (
            <div className="absolute z-10 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              <button
                type="button"
                onClick={() => {
                  handleVideo({
                    vid: media[mediaIndex]?.src?.jwvid,
                    destinationName: destination.name,
                  });
                  tagular("click", {
                    actionOutcome: "PLAY",
                    outboundUrl: "null",
                    webElement: {
                      location: "BITHOME",
                      elementType: "BUTTON",
                      position: "VIDEO",
                      text: destination.name,
                      name: "DESTINATION",
                    },
                  });
                }}
              >
                <div className="flex items-center justify-center rounded-full shadow-lg w-14 h-14 bg-blue">
                  <PlayFill className="text-white " />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

DestinationSection.propTypes = {
  destination: PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    elsewhere: PropTypes.string,
    meta: PropTypes.shape({
      category: PropTypes.string.isRequired,
      parent: PropTypes.string.isRequired,
    }).isRequired,
    editorial: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(["p", "ol", "blockquote", "video"]),
        children: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.arrayOf(PropTypes.string),
        ]),
      })
    ).isRequired,
    media: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(["video", "image"]).isRequired,
        credit: PropTypes.string,
        premier: PropTypes.bool,
        src: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            poster: PropTypes.string,
            mp4: PropTypes.string,
            webm: PropTypes.string,
            jwvid: PropTypes.string,
            src: PropTypes.string,
            credit: PropTypes.string,
          }).isRequired,
        ]),
      })
    ),
    articles: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  totalDestinations: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};
